import * as React from "react";
import toast from "react-hot-toast";
import { DownloadType } from "../admin/videos/selectedVideoCardV2/helpers";
import { filterRealFiles } from "../videoProduction/helper";
import { DownloadVideoModal } from "../../components/MenuOptions";
import { DownloadDropdown } from "../../components/DownloadDropdown";
import { DownloadOverlay } from "../../components/DownloadOverlay";
import axiosInstant from "../../api";
import { getBrandOrder } from "../../api/endpoints";
import { formatVideoName } from "../admin/videos/hooks/useFetchVideos";
import {
  buildProjectsFolderResolver,
  buildZipSuffix,
  collectZipKeys,
  submitZip,
  isTimeoutError,
} from "../../utils/downloadHelpers";
import { useDownloads } from "../../components/Downloads";
import { useUser } from "../../hooks/useUser";
import { isInternal } from "../../utils";
import { CommentsMenu } from "../../components/CommentsMenu";
import {
  CommentsFormat,
  downloadOrderComments,
  fetchProjectComments,
} from "../../utils/commentsExport";

interface Props {
  orderId: number;
  orderName: string;
  disabled?: boolean;
  allowedFileTypes?: Set<string> | null;
  commentCount?: number;
}

export function OrderDownloadBtn({
  orderId,
  orderName,
  commentCount,
  disabled = false,
  allowedFileTypes = null,
}: Props) {
  const [showOptions, setShowOptions] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [downloadLink, setDownloadLink] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false);
  // Long-running zips are handed off to the global download tracker (top bar).
  const { promote } = useDownloads();
  const { role } = useUser();

  React.useEffect(() => {
    if (downloadLink !== "") setModalOpen(true);
  }, [downloadLink]);

  const handleSelect = async (type: DownloadType) => {
    setShowOptions(false);
    setDownloading(true);
    setDownloadLink("");
    setModalOpen(false);

    const zipFileName = `${formatVideoName(orderName)}${buildZipSuffix(type)}.zip`;
    const roomId = Math.random().toString(36).substring(2, 15);

    try {
      const response = await axiosInstant.get(getBrandOrder(orderId));
      const order = response.data;
      const allFiles = (order.projects ?? []).flatMap((p: any) =>
        filterRealFiles(p.projectFiles),
      );

      if (!allFiles.length) {
        toast.error("No files available to download.");
        return;
      }

      const loadingId = toast.loading("Zipping files, please wait...");
      const { files, hasContent } = await collectZipKeys(
        allFiles,
        type,
        allowedFileTypes,
        buildProjectsFolderResolver(order.projects),
        isInternal(role) || !!order.brand?.pre_approval_downloading,
      );
      toast.dismiss(loadingId);

      if (!hasContent) {
        toast.error("No content available to download.");
        return;
      }

      const fileName = await submitZip(
        files,
        zipFileName,
        roomId,
        type === DownloadType.ALL ? { orderId } : undefined,
      );
      if (fileName) setDownloadLink(fileName);
    } catch (error: any) {
      if (isTimeoutError(error)) {
        // Long-running zip: the global tracker's top bar takes over.
        promote(roomId, zipFileName);
      } else {
        toast.error(error.message ?? "Download failed");
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleSelectComments = async (format: CommentsFormat) => {
    setShowOptions(false);
    setDownloading(true);
    const toastId = toast.loading("Preparing comments...");
    try {
      const { data: order } = await axiosInstant.get(getBrandOrder(orderId));
      const settled = await Promise.allSettled(
        (order.projects ?? []).map((project: any) =>
          fetchProjectComments(
            project.id,
            project.projectFiles ?? [],
            project.content_channels,
            project.title,
          ),
        ),
      );
      const projects = settled
        .filter((r): r is PromiseFulfilledResult<any> => r.status === "fulfilled")
        .map((r) => r.value);
      const sent = await downloadOrderComments(orderName, projects, format);
      if (!sent) {
        toast.error("No comments have been left on this order.", { id: toastId });
        return;
      }
      toast.success("Comments downloaded.", { id: toastId });
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ?? error?.message ?? "Failed to download comments.",
        { id: toastId },
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <DownloadOverlay visible={downloading} />

      {!downloading && commentCount !== 0 && (
        <CommentsMenu onSelect={handleSelectComments} />
      )}

      {!downloading && (
        <DownloadDropdown
          small
          isOpen={showOptions}
          isDownloading={false}
          disabled={disabled}
          onToggle={() => setShowOptions((v) => !v)}
          onSelect={handleSelect}
          onClickAway={() => setShowOptions(false)}
          allowedFileTypes={allowedFileTypes}
        />
      )}

      <DownloadVideoModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setDownloadLink("");
        }}
        downloadSource={downloadLink}
      />
    </>
  );
}
